import { useRequest } from "~/v2/api/hooks";
import { getStockSnapshots } from "~/v2/api/rest/data";
import { OPTION_CHAIN_SNAPSHOTS_POLLING_INTERVAL } from "~/v2/constants";
import { toEpochMs } from "~/v3-poc/utils/time";

/** Same cadence as the chain, so spot and strikes never disagree on screen. */
const SPOT_POLLING_INTERVAL = OPTION_CHAIN_SNAPSHOTS_POLLING_INTERVAL;

export interface UnderlyingSpot {
  /** Latest trade while the session is open, else the daily close. */
  price?: number;
  /** Timestamp of whichever value `price` came from. */
  at?: number;
  /** True when `price` fell back to the daily close. */
  isClosingPrice: boolean;
}

/**
 * Underlying price for at-the-money and in-the-money treatment.
 *
 * After the close the last trade stops moving, so the daily close stands in —
 * the PRD asks for exactly that, and it keeps the shading meaningful overnight.
 */
export const useUnderlyingSpot = ({
  symbol,
  enabled = true,
}: {
  symbol?: string;
  enabled?: boolean;
}): UnderlyingSpot & { isLoading: boolean } => {
  const { data, isLoading } = useRequest(
    ["v3-poc", "options-chain", "underlying-spot", symbol ?? ""],
    () => getStockSnapshots([symbol ?? ""]),
    {
      enabled: enabled && !!symbol,
      refetchInterval: SPOT_POLLING_INTERVAL,
      staleTime: SPOT_POLLING_INTERVAL,
      meta: { showErrorToast: false },
    },
  );

  const snapshot = symbol ? data?.[symbol] : undefined;
  const tradePrice = snapshot?.latestTrade?.p;
  const closePrice = snapshot?.dailyBar?.c;

  if (tradePrice !== undefined) {
    return {
      price: tradePrice,
      at: toEpochMs(snapshot?.latestTrade?.t),
      isClosingPrice: false,
      isLoading,
    };
  }

  return {
    price: closePrice,
    at: toEpochMs(snapshot?.dailyBar?.t),
    isClosingPrice: closePrice !== undefined,
    isLoading,
  };
};
